import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import EventBoard from "../components/EventBoard";
import SpecialOffersSection from "../components/SpecialOffers";
import WelcomeOffer from "../components/WelcomeOffer";
import ApexFinder from "../components/ApexFinder";
import { useAuth } from "../lib/auth";
import { useCurrency } from "../lib/currency";
import { Crown, Sparkles, ShieldCheck, Wallet, ArrowRight, CheckCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const FAQS = [
  ["What is Arthur's Vault?", "An independent premium marketplace for Age of Empires Mobile Apex top-ups. Every purchase earns Apex Rewards that grow with your loyalty tier."],
  ["Do I need an account?", "Yes. Every purchase requires an Arthur's Vault account so we can track your orders, earn you rewards, and prevent fraud."],
  ["What are Apex Rewards?", "Store credit in GBP earned on qualifying purchases. Redeem them on any future order."],
  ["How much of an order can I pay with Apex Rewards?", "Apex Rewards are capped at 5% of the total purchase on any single order. There's no limit to how often you use them — every order gets its own 5% allowance."],
  ["How is my Apex Rewards balance calculated?", "All percentage-based rewards (loyalty, referral, Pay by Bank, Vault Offers) are calculated from your order's Qualifying Amount — the amount you're actually paying after promo discounts and rewards redemption."],
  ["How do loyalty tiers work?", "Your qualifying spend determines your tier (Bronze → Apex Elite). Higher tiers earn a higher reward rate on every order."],
  ["Why does Pay by Bank earn extra?", "Pay by Bank costs us less than card processing, so we share the saving with you — an extra +1% Apex Rewards on every Pay by Bank purchase (where available in your region)."],
  ["How does the referral programme work?", "Every account has a unique referral code you can share as many times as you like. When someone new to Arthur's Vault uses your code on their first qualifying order, they earn an extra bonus and you earn a referrer reward — both credited as Apex Rewards once their order is delivered."],
  ["Is there a limit on how many people I can refer?", "No. Your referral code has unlimited uses — invite as many friends as you like. The referrer reward is capped per successful referral, but there's no cap on how many successful referrals you can rack up."],
  ["Is there a cap on the referrer reward?", "Yes. The referrer reward is capped per successful referral, regardless of how large your friend's order is. Every extra successful referral still earns you the reward up to that cap."],
  ["What are referral milestones?", "As you rack up successful referrals you unlock milestone rewards — currently issued as bonus Apex Rewards, though the specific gift for each milestone may vary and could be delivered as a promo code or other credit at Arthur's Vault's discretion."],
  ["What counts as a successful referral?", "A referral becomes successful once the referred customer completes a qualifying order that meets the minimum Qualifying Amount and their delivery is validated. Cancelled, refunded, or fraudulent orders don't count."],
  ["Can I use a referral code on any order?", "Only on your very first Arthur's Vault order, and only if you're new to the store. Once your first order is submitted the referral entry is retired — but your own code stays visible for you to keep sharing."],
  ["Is payment secure?", "All payments go through Stripe with server-side verification. We never store your card details."],
];

export default function Home() {
  const [pkgs, setPkgs] = useState([]);
  const [reviews, setReviews] = useState({ average: 0, count: 0, reviews: [] });
  const [offers, setOffers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [eventCount, setEventCount] = useState(0);
  const { user } = useAuth();
  const { format, code, isBase } = useCurrency();
  const location = useLocation();
  useEffect(() => {
    api.get("/apex-products").then(r => setPkgs(r.data));
    api.get("/reviews").then(r => setReviews(r.data));
    api.get("/vault-offers").then(r => setOffers(r.data));
    api.get("/loyalty/tiers").then(r => setTiers(r.data));
    api.get("/event-board").then(r => setEventCount((r.data || []).length)).catch(() => setEventCount(0));
  }, []);
  const scrollToEventBoard = (e) => {
    e.preventDefault();
    const el = document.getElementById("event-board");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  // Scroll to hash anchor (#offers, #rewards, #faq) whenever the URL hash
  // changes OR the section content finishes loading. React Router doesn't
  // scroll automatically for hash-only Link changes.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    // Retry a few times because sections mount after data fetch (offers, tiers).
    let tries = 0;
    const iv = setInterval(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        clearInterval(iv);
      } else if (++tries > 20) {
        clearInterval(iv);
      }
    }, 100);
    return () => clearInterval(iv);
  }, [location.hash, offers.length, tiers.length]);
  return (
    <div>
      <Header />
      {/* HERO */}
      <section className="pt-32 pb-20 px-4 sm:px-8 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="mono-caption mb-4">Independent Premium Marketplace</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mb-6">
              <span className="gold-text">Arthur's Vault</span>
              <br/><span className="text-[var(--av-text)]">Premium Apex Top-Ups.</span>
            </h1>
            <p className="text-lg text-[var(--av-text-dim)] max-w-xl mb-8">Purchase Apex, earn <span className="purple-text font-semibold">Apex Rewards</span>, and unlock more from the Vault every time you return.</p>
            <div className="flex flex-wrap gap-3">
              <Link to={user ? "/my-vault" : "/auth?mode=login"} data-testid="enter-vault-btn" className="btn-gold shimmer">Enter the Vault <ArrowRight className="inline w-4 h-4 ml-1"/></Link>
              {eventCount > 0 && (
                <a
                  href="#event-board"
                  onClick={scrollToEventBoard}
                  data-testid="event-board-btn"
                  className="btn-outline-gold"
                >
                  Event Board
                </a>
              )}
              <Link to="/store" data-testid="view-apex-btn" className="btn-outline-gold">View Apex</Link>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-10 text-sm">
              <div className="vault-card p-4"><ShieldCheck className="w-5 h-5 text-[var(--av-gold)] mb-2"/><div className="font-semibold">Secure Checkout</div><div className="text-[var(--av-text-dim)] text-xs mt-1">Stripe verified payments</div></div>
              <div className="vault-card p-4"><Wallet className="w-5 h-5 text-[var(--av-purple)] mb-2"/><div className="font-semibold">Apex Rewards</div><div className="text-[var(--av-text-dim)] text-xs mt-1">Earn on every order</div></div>
              <div className="vault-card p-4"><Crown className="w-5 h-5 text-[var(--av-gold)] mb-2"/><div className="font-semibold">6 Loyalty Tiers</div><div className="text-[var(--av-text-dim)] text-xs mt-1">Up to 3% back</div></div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="vault-card p-6 relative overflow-hidden">
              <div className="relative rounded-lg overflow-hidden mb-4 aspect-[4/3]">
                <img
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_arthur-vault-build/artifacts/dtlom19q_1ab22d5b-8037-4fb3-b322-8ba6bc0a96a3.png"
                  alt="Arthur's Vault golden vault overflowing with gold coins"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0C10]/40 via-transparent to-[#9400D3]/15 pointer-events-none"></div>
                <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#0B0C10]/60 to-transparent pointer-events-none"></div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="mono-caption">Live Store</div>
                  <div className="gold-text font-serif text-2xl font-bold">OPEN</div>
                </div>
                <span className="pill pill-purple"><Sparkles className="w-3 h-3"/> Rewards Active</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* APEX FINDER — "How much Apex do you need?" recommender.
          Placed right after the hero per owner spec: Hero → Apex Finder → Event Board.
          Compact card. Server-authoritative recommendation. */}
      <ApexFinder />

      {/* EVENT BOARD — admin-controlled announcement carousel. Auto-hides if empty. */}
      <EventBoard />

      {/* Ordering note preserved above. */}

      {/* WELCOME OFFER — one-time, only rendered when the backend confirms
          the signed-in user is eligible. Server-side eligibility check
          means refreshing / incognito / new devices can't unlock it. */}
      <WelcomeOffer />

      {/* DAILY / WEEKLY / MONTHLY OFFERS — mirrored from the Store so
          returning customers see the deal on the home page too. Same
          component so pricing, currency conversion, countdowns and
          test-mode badge stay in one place. */}
      <section className="px-4 sm:px-8 max-w-7xl mx-auto mb-12">
        <SpecialOffersSection/>
      </section>

      {/* POPULAR PACKAGES */}
      <section className="py-16 px-4 sm:px-8 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="mono-caption">Popular Apex Packages</div>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold mt-2">Choose Your <span className="gold-text">Apex</span></h2>
          </div>
          <Link to="/store" className="btn-outline-gold text-sm">View All →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pkgs.slice(0, 6).map(p => {
            const start = p.tiers?.[0]?.unit_price_gbp || 0;
            const cheapest = p.tiers ? Math.min(...p.tiers.map(t => t.unit_price_gbp)) : start;
            const isBestValue = p.apex_amount === 499;
            const isOwnersPick = p.apex_amount === 999;
            const isFeatured = isBestValue || isOwnersPick;
            const cardCls = isBestValue
              ? "vault-card p-6 relative overflow-hidden ring-2 ring-[var(--av-gold)] shadow-[0_0_30px_-8px_rgba(212,175,55,0.55)]"
              : isOwnersPick
                ? "vault-card p-6 relative overflow-hidden ring-2 ring-[var(--av-purple)] shadow-[0_0_28px_-10px_rgba(148,0,211,0.55)]"
                : "vault-card p-6 relative overflow-hidden";
            return (
            <div key={p.id} data-testid={`pkg-card-${p.apex_amount}`} className={cardCls}>
              {isFeatured && (
                <div
                  data-testid={`home-ribbon-${p.apex_amount}`}
                  className={`absolute top-3 -right-8 rotate-45 w-32 text-center py-1 text-[10px] font-serif font-bold tracking-wider uppercase shadow-lg ${
                    isBestValue
                      ? "bg-gradient-to-r from-[#d4af37] to-[#a17c1a] text-[#0b0c10]"
                      : "bg-gradient-to-r from-[#9400D3] to-[#5b1c8a] text-white"
                  }`}
                >
                  {isBestValue ? "★ Best Value" : "★ Owner's Pick"}
                </div>
              )}
              <span className={`pill ${isFeatured ? (isBestValue ? "pill-gold" : "pill-purple") : "pill-gold"} mb-3`}>
                {isBestValue ? "CHEAPEST PER APEX" : isOwnersPick ? "OWNER FAVOURITE" : "DYNAMIC BULK"}
              </span>
              <div className="text-[var(--av-text-dim)] text-sm mb-1">1–100 packs</div>
              <div className="font-serif text-3xl font-bold gold-text mb-2">{p.apex_amount.toLocaleString()} Apex</div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-2xl font-bold">{format(start)}</span>
                {p.rrp_unit_gbp && p.rrp_unit_gbp > start && (
                  <span className="text-sm text-[var(--av-text-muted)] line-through decoration-[var(--av-purple)]/70">{format(p.rrp_unit_gbp)}</span>
                )}
              </div>
              <div className="text-[11px] text-[var(--av-text-dim)] mb-3">from · {format(cheapest)} at 100</div>
              {p.rrp_unit_gbp && p.rrp_unit_gbp > cheapest && (
                <div className="text-[11px] purple-text font-semibold mb-3">Save up to {Math.round((1 - cheapest / p.rrp_unit_gbp) * 100)}%</div>
              )}
              <div className="pill pill-purple mb-4">Earn ~{format(start*0.015)}+ Apex Rewards</div>
              <Link to={`/apex/${p.id}`} data-testid={`pkg-buy-${p.apex_amount}`} className="btn-gold w-full text-center block">Configure</Link>
              {!isBase && <div className="text-[10px] text-[var(--av-text-muted)] mt-2 text-center">Approximate · charged in GBP at Stripe — your bank's rate on the day sets the exact debit</div>}
            </div>
          );})}
        </div>
      </section>

      {/* HOW REWARDS WORK */}
      <section id="rewards" className="py-16 px-4 sm:px-8 max-w-7xl mx-auto">
        <div className="mono-caption text-center">How It Works</div>
        <h2 className="font-serif text-3xl lg:text-4xl font-bold text-center mt-2 mb-10">Every Purchase <span className="purple-text">Rewards You</span></h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            ["Purchase Apex", "Buy any package. Rewards are earned as a percentage of qualifying payment.", <Wallet className="w-6 h-6"/>],
            ["Unlock Loyalty", "As your qualifying spend grows, your reward rate rises across 6 tiers up to Apex Elite (3%).", <Crown className="w-6 h-6"/>],
            ["Redeem on Future Orders", "Use up to 5% of any order's value in Apex Rewards. £1 minimum redemption.", <Sparkles className="w-6 h-6"/>],
          ].map(([t, d, i]) => (
            <div key={t} className="vault-card p-6">
              <div className="text-[var(--av-gold)] mb-3">{i}</div>
              <div className="font-serif font-bold text-xl mb-2">{t}</div>
              <p className="text-sm text-[var(--av-text-dim)]">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LOYALTY TIERS */}
      <section className="py-16 px-4 sm:px-8 max-w-7xl mx-auto">
        <h2 className="font-serif text-3xl lg:text-4xl font-bold text-center mb-10">Loyalty <span className="gold-text">Tiers</span></h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {tiers.map(t => (
            <div key={t.name} className={`vault-card p-5 text-center ${t.name === "Apex Elite" ? "border-[var(--av-purple)]" : ""}`}>
              <div className="text-2xl mb-1" style={{color: t.color}}>◆</div>
              <div className="font-serif font-bold">{t.name}</div>
              <div className="text-xs text-[var(--av-text-dim)] mt-1">£{t.min_spend.toLocaleString()}+</div>
              <div className="mono-caption mt-3" style={{color: t.name === "Apex Elite" ? "#E9B4FF" : "#D4AF37"}}>{t.reward_rate}% back</div>
            </div>
          ))}
        </div>
      </section>

      {/* VAULT OFFERS — anchor always renders so the header's "Vault Offers"
          nav link always has somewhere to scroll to, even when the store
          has no active offers. */}
      <section id="offers" className="py-16 px-4 sm:px-8 max-w-7xl mx-auto">
        <h2 className="font-serif text-3xl lg:text-4xl font-bold mb-8">Vault <span className="purple-text">Offers</span></h2>
        {offers.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-6">
            {offers.map(o => (
              <div key={o.id} className="vault-card p-6 border-[var(--av-purple)]">
                <span className="pill pill-purple mb-3">Vault Offer</span>
                <div className="font-serif font-bold text-xl mb-2">{o.name}</div>
                <p className="text-sm text-[var(--av-text-dim)] mb-4">{o.description}</p>
                <div className="mono-caption purple-text">
                  {o.reward_type === "percentage" ? `+${o.reward_percentage}% Apex Rewards` : `£${o.reward_fixed_gbp.toFixed(2)} Apex Rewards`}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="vault-card p-8 text-center border-[var(--av-purple)]/40">
            <div className="mono-caption purple-text mb-2">Stay Tuned</div>
            <p className="text-sm text-[var(--av-text-dim)]">
              No active Vault Offers right now — new bonus-reward promotions drop regularly. Follow the Vault to catch the next one.
            </p>
          </div>
        )}
      </section>

      {/* HOW IT WORKS STEPS */}
      <section className="py-16 px-4 sm:px-8 max-w-7xl mx-auto">
        <h2 className="font-serif text-3xl lg:text-4xl font-bold text-center mb-10">How It <span className="gold-text">Works</span></h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          {["Create Account","Choose Apex","Select Game Profile","Pay Securely","We Fulfil","Confirm Delivery","Earn Rewards"].map((s, i)=> (
            <div key={s} className="vault-card p-5">
              <div className="mono-caption mb-2">Step {i+1}</div>
              <div className="font-semibold flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[var(--av-gold)]"/>{s}</div>
            </div>
          ))}
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-16 px-4 sm:px-8 max-w-7xl mx-auto">
        <h2 className="font-serif text-3xl lg:text-4xl font-bold mb-6">Verified <span className="gold-text">Reviews</span></h2>
        <div className="flex items-center gap-4 mb-8">
          <div className="font-serif text-4xl font-bold gold-text">{reviews.average.toFixed(1)}</div>
          <div className="text-sm text-[var(--av-text-dim)]">{reviews.count} verified purchase{reviews.count !== 1 ? "s":""}</div>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {reviews.reviews.slice(0, 3).map(r => (
            <div key={r.id} className="vault-card p-6">
              <span className="pill pill-gold mb-2">Verified Purchase</span>
              <div className="text-[var(--av-gold)] my-2">{"★".repeat(r.rating)}<span className="text-[var(--av-text-muted)]">{"★".repeat(5-r.rating)}</span></div>
              <div className="font-semibold mb-1">{r.title}</div>
              <p className="text-sm text-[var(--av-text-dim)]">{r.body}</p>
              <div className="mono-caption mt-3">— {r.customer_name}</div>
            </div>
          ))}
          {reviews.reviews.length === 0 && <div className="text-[var(--av-text-dim)] text-sm">Reviews will appear here once verified customers post them.</div>}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 px-4 sm:px-8 max-w-4xl mx-auto">
        <h2 className="font-serif text-3xl lg:text-4xl font-bold mb-8 text-center">Frequently Asked</h2>
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map(([q,a])=>(
            <AccordionItem key={q} value={q} data-testid={`faq-${q.slice(0,20)}`} className="border-b border-[var(--av-border)]">
              <AccordionTrigger className="text-left text-[var(--av-text)] hover:text-[var(--av-gold)]">{q}</AccordionTrigger>
              <AccordionContent className="text-[var(--av-text-dim)]">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <Footer/>
    </div>
  );
}
